import React from 'react';
import { Link } from 'react-router-dom';

import SubtleBadge from 'components/common/SubtleBadge';

import paths from 'routes/paths';
import { formatStatus } from 'utils/valueFormatters';
import { getOrderBadgeColor } from 'constants/order';
import { getShipmentBadgeColor } from 'constants/shipment';
import { TIME_OPTION } from 'constants/time';

export const getOrderListColumns = (tenant, warehouse) => [
  {
    accessorKey: 'channel_order_id',
    header: 'Order#',
    enableSorting: true,
    meta: {
      valueType: 'string',
    },
    cell: ({ row: { original } }) => {
      const { id, channel_order_id } = original;

      return (
        <Link to={paths.orders.details(tenant, warehouse, id)}>
          <strong>{channel_order_id}</strong>
        </Link>
      );
    },
  },
  {
    accessorKey: 'created',
    header: 'WMS Created',
    enableSorting: true,
    meta: {
      valueType: 'string',
    },
    cell: ({ row: { original } }) => {
      const { created } = original;

      return (
        <span>{new Date(created).toLocaleString('en-IN', TIME_OPTION)}</span>
      );
    },
  },
  {
    accessorKey: 'channel',
    header: 'Channel',
    enableSorting: true,
    meta: {
      valueType: 'string',
    },
    cell: ({ row: { original } }) => {
      const { channel } = original;

      return <span>{channel?.channel_type}</span>;
    },
  },
  {
    accessorKey: 'order_status',
    header: 'Order Status',
    enableSorting: false,
    meta: {
      headerProps: { className: 'text-center' },
      cellProps: { className: 'text-center' },
    },
    cell: ({ row: { original } }) => {
      const { order_status } = original;

      return (
        <SubtleBadge pill bg={getOrderBadgeColor(order_status)}>
          {formatStatus(order_status)}
        </SubtleBadge>
      );
    },
  },
  {
    accessorKey: 'payment_type',
    header: 'Payment',
    enableSorting: true,
    meta: {
      valueType: 'string',
    },
    cell: ({ row: { original } }) => {
      const { payment_type, payment_currency, payment_order_amount } = original;

      return (
        <>
          <div className="mb-1">
            <strong>Method: </strong>
            <span className="text-uppercase">{payment_type}</span>
          </div>
          <div>
            <strong>Amount: </strong>
            <span>{`${payment_currency} ${payment_order_amount}`}</span>
          </div>
        </>
      );
    },
  },
  {
    accessorKey: 'channel_on_hold',
    header: 'On Hold',
    enableSorting: false,
    meta: {
      headerProps: { className: 'text-center' },
      cellProps: { className: 'text-center' },
    },
    cell: ({ row: { original } }) => {
      const { channel_on_hold } = original;

      return <span>{channel_on_hold ? 'Yes' : 'No'}</span>;
    },
  },
  {
    accessorKey: 'lines',
    header: 'Products',
    enableSorting: false,
    meta: {
      valueType: 'string',
      minWidth: '300px',
      maxWidth: '300px',
    },
    cell: ({ row: { original } }) => {
      const { lines } = original;

      if (!lines?.length) return <span>-</span>;

      return (
        <div className="d-flex flex-column gap-2">
          {lines.map((line, idx) => (
            <div
              key={line?.id}
              className={`pb-1 ${
                idx === lines.length - 1 ? '' : 'border-bottom'
              }`}
            >
              <div
                className="text-truncate"
                title={line?.channel_product?.channel_product_name || '-'}
              >
                <strong>Name: </strong>
                {line?.channel_product?.channel_product_name || '-'}
              </div>
              <div>
                <strong>SKU: </strong>
                {line?.channel_product?.channel_product_sku || '-'}
              </div>
              <div>
                <strong>Quantity: </strong>
                {Number(line.quantity).toLocaleString()}
              </div>
            </div>
          ))}
        </div>
      );
    },
  },
  {
    accessorKey: 'channel_order_created',
    header: 'Channel Created',
    enableSorting: true,
    meta: {
      valueType: 'string',
    },
    cell: ({ row: { original } }) => {
      const { channel_order_created } = original;

      return (
        <span>
          {new Date(channel_order_created).toLocaleString('en-IN', TIME_OPTION)}
        </span>
      );
    },
  },
];

export const getOrderDetailItemsColumns = (tenant, warehouse) => [
  {
    accessorKey: 'channel_title',
    header: 'Product Description',
    meta: {
      valueType: 'string',
    },
    cell: ({ row: { original } }) => {
      const { channel_product_key, channel_variant_key, channel_title } =
        original;

      return `${channel_product_key}-${channel_variant_key}-${channel_title}`;
    },
  },
  {
    accessorKey: 'channel_sku',
    header: 'Channel SKU',
    meta: {
      valueType: 'string',
    },
    cell: ({ row: { original } }) => {
      const { channel_sku } = original;

      return <span>{channel_sku || '-'}</span>;
    },
  },
  {
    accessorKey: 'payment_total_price',
    header: 'Price Info',
    meta: {
      valueType: 'number',
    },
    cell: ({ row: { original } }) => {
      const { shipment_lines } = original;

      const sumOfUnitGrossAmount = shipment_lines.reduce((sum, line) => {
        return sum + (parseFloat(line.unit_gross_amount) || 0);
      }, 0);

      return <span>{sumOfUnitGrossAmount.toFixed(2)}</span>;
    },
  },
  {
    accessorKey: 'shipment_line_total_quantity',
    header: 'Total Units',
    meta: {
      valueType: 'number',
    },
    cell: ({ row: { original } }) => {
      const shipmentLineSummary = original.shipment_line_summary;
      const { shipment_line_total_quantity } = shipmentLineSummary;

      return shipment_line_total_quantity.toLocaleString() || 0;
    },
  },
  {
    accessorKey: 'in_process',
    header: 'In Process',
    meta: {
      valueType: 'number',
    },
    cell: ({ row: { original } }) => {
      const { channel_product_id, product_id } = original;

      if (channel_product_id === null || product_id === null) {
        return 0;
      } else {
        const { shipment_line_summary, shipment_lines } = original;

        const inProcessingValue =
          shipment_line_summary.shipment_line_created_quantity +
          shipment_line_summary.shipment_line_picking_quantity +
          shipment_line_summary.shipment_line_picked_quantity;

        let inventoryMissingValue = 0;
        shipment_lines.forEach(line => {
          if (line.is_cancelable && line.product_batch_id === null) {
            inventoryMissingValue += 1;
          }
        });
        const result = inProcessingValue - inventoryMissingValue;

        return result.toLocaleString() || 0;
      }
    },
  },
  {
    accessorKey: 'cancelled',
    header: 'Cancelled',
    meta: {
      valueType: 'number',
    },
    cell: ({ row: { original } }) => {
      const { shipment_line_summary } = original;

      const value =
        shipment_line_summary.shipment_line_order_cancelled_quantity +
        shipment_line_summary.shipment_line_reject_not_found_quantity +
        shipment_line_summary.shipment_line_reject_damaged_quantity;

      return value.toLocaleString() || 0;
    },
  },
  {
    accessorKey: 'oos',
    header: 'OOS',
    meta: {
      valueType: 'number',
    },
    cell: ({ row: { original } }) => {
      const { channel_product_id, product_id } = original;

      if (channel_product_id === null || product_id === null) {
        return 0;
      } else {
        const { shipment_lines } = original;

        let value = 0;
        shipment_lines.forEach(line => {
          if (line.is_cancelable && line.product_batch_id === null) {
            value += 1;
          }
        });

        return value.toLocaleString() || 0;
      }
    },
  },
  {
    accessorKey: 'notice',
    header: 'Notice',
    meta: {
      valueType: 'string',
    },
    cell: ({ row: { original } }) => {
      const { channel_product_id, product_id, order_line_status } = original;

      if (['created', 'processing'].includes(order_line_status)) {
        if (channel_product_id === null) {
          return 'Channel Product Required';
        } else if (product_id === null) {
          return 'Product Disconnected';
        }
      }

      return '';
    },
  },
];

export const getOrderDetailShipmentColumns = (tenant, warehouse) => [
  {
    accessorKey: 'shipment_code',
    header: 'Shipment Id',
    meta: {
      valueType: 'string',
    },
    cell: ({ row }) => row.original.shipment_code,
  },
  {
    accessorKey: 'shipment_status',
    header: 'Shipment Status',
    meta: {
      headerProps: { className: 'text-center' },
      cellProps: { className: 'text-center' },
    },
    cell: ({ row }) => {
      const { shipment_status } = row.original;

      return (
        <SubtleBadge pill bg={getShipmentBadgeColor(shipment_status)}>
          {formatStatus(shipment_status)}
        </SubtleBadge>
      );
    },
  },
  {
    accessorKey: 'created',
    header: 'Created On',
    meta: {
      valueType: 'string',
    },
    cell: ({ row }) =>
      new Date(row.original.created).toLocaleString('en-IN', TIME_OPTION),
  },
];

export const getOrderDetailInvoicesColumns = (tenant, warehouse) => [
  {
    accessorKey: 'shipment.shipment_code',
    header: 'Invoice #',
    meta: {
      valueType: 'string',
    },
    cell: ({ row }) => row.original.shipment.shipment_code,
  },
  {
    accessorKey: 'invoice_lines.product_sku',
    header: 'No. of SKUs',
    meta: {
      valueType: 'number',
    },
    cell: ({ row }) => row.original.invoice_lines.length || 0,
  },
  {
    accessorKey: 'invoice_lines.total_rate',
    header: 'Selling Price w/o Taxes',
    meta: {
      valueType: 'number',
    },
    cell: ({ row: { original } }) => {
      const { invoice_lines } = original;

      const sumOfTotalRate = invoice_lines.reduce((sum, line) => {
        return sum + (parseFloat(line.total_rate) || 0);
      }, 0);

      return <span>{sumOfTotalRate.toFixed(2)}</span>;
    },
  },
  {
    accessorKey: 'invoice_lines.total_discount',
    header: 'Discount',
    meta: {
      valueType: 'number',
    },
    cell: ({ row: { original } }) => {
      const { invoice_lines } = original;

      const sumOfTotalDiscount = invoice_lines.reduce((sum, line) => {
        return sum + (parseFloat(line.total_discount) || 0);
      }, 0);

      return <span>{sumOfTotalDiscount.toFixed(2)}</span>;
    },
  },
  {
    accessorKey: 'invoice_lines',
    header: 'Sub Total',
    meta: {
      valueType: 'number',
    },
    cell: ({ row: { original } }) => {
      const { invoice_lines } = original;

      const sumOfTotalRate = invoice_lines.reduce((sum, line) => {
        return sum + (parseFloat(line.total_rate) || 0);
      }, 0);

      return <span>{sumOfTotalRate.toFixed(2)}</span>;
    },
  },
  {
    accessorKey: 'invoice_lines.total_igst',
    header: 'Taxes',
    meta: {
      valueType: 'number',
    },
    cell: ({ row: { original } }) => {
      const { invoice_lines } = original;

      const totalTax = invoice_lines.reduce((sum, line) => {
        const igst = parseFloat(line.total_igst) || 0;
        const cgst = parseFloat(line.total_cgst) || 0;
        const sgst = parseFloat(line.total_sgst) || 0;

        return sum + igst + cgst + sgst;
      }, 0);

      return <span>{totalTax.toFixed(2)}</span>;
    },
  },
  {
    accessorKey: 'shipment.shipping_charges',
    header: 'Charges',
    meta: {
      valueType: 'number',
    },
    cell: ({ row: { original } }) => {
      const { shipment } = original;

      const totalCharges =
        parseFloat(shipment.cash_on_delivery_charges) +
        parseFloat(shipment.shipping_charges);

      return <span>{totalCharges.toFixed(2)}</span>;
    },
  },
  {
    accessorKey: 'invoice_lines.total_amount',
    header: 'Total',
    meta: {
      valueType: 'number',
    },
    cell: ({ row: { original } }) => {
      const { invoice_lines, shipment } = original;

      const sumOfTotalAmount = invoice_lines.reduce((sum, line) => {
        return sum + (parseFloat(line.total_amount) || 0);
      }, 0);

      const totalCharges =
        parseFloat(shipment.cash_on_delivery_charges) +
        parseFloat(shipment.shipping_charges);

      const total = sumOfTotalAmount + totalCharges;

      return <span>{total.toFixed(2)}</span>;
    },
  },
  {
    accessorKey: 'shipment.invoice_date',
    header: 'Created On',
    meta: {
      valueType: 'string',
    },
    cell: ({ row: { original } }) => {
      const { shipment } = original;

      return (
        <span>
          {new Date(shipment.invoice_date).toLocaleString('en-IN', TIME_OPTION)}
        </span>
      );
    },
  },
];

export const getOrderDetailReturnsColumns = (tenant, warehouse) => [
  {
    accessorKey: 'return_code',
    header: 'Return Code',
    meta: {
      valueType: 'string',
    },
    cell: ({ row: { original } }) => {
      const { return_code } = original;

      return <span>{return_code || '-'}</span>;
    },
  },
  {
    accessorKey: 'shipment_status',
    header: 'Shipment Status',
    meta: {
      headerProps: { className: 'text-center' },
      cellProps: { className: 'text-center' },
    },
    cell: ({ row: { original } }) => {
      const { shipment_status } = original;

      return (
        <SubtleBadge pill bg={getShipmentBadgeColor(shipment_status)}>
          {formatStatus(shipment_status)}
        </SubtleBadge>
      );
    },
  },
  {
    accessorKey: 'return_acknowledged_at',
    header: 'Created At',
    meta: {
      valueType: 'string',
    },
    cell: ({ row: { original } }) => {
      const { return_acknowledged_at } = original;

      return (
        <span>
          {return_acknowledged_at
            ? new Date(return_acknowledged_at).toLocaleString(
                'en-IN',
                TIME_OPTION,
              )
            : '-'}
        </span>
      );
    },
  },
];
